import React, { useState } from 'react';
import { POSTERS } from '../../data/products';
import { useCart } from '../../contexts/CartContext';

const VINTAGE_COLORS = {
  paper: "#e8dcc4",
  paperDark: "#d4c4a8",
  ink: "#2a1810",
  inkFaded: "#5a4030",
  red: "#8b2500",
  redDark: "#5c1a00",
  gold: "#b8860b",
};

const YUGA_COLORS = {
  satya: "#d4a44a",
  treta: "#d97a3c",
  dvapara: "#a8c8e8",
  kali: "#6a6058"
};

function OrnateCorner({ rotation = 0 }) {
  return (
    <svg 
      width="40" height="40" viewBox="0 0 40 40" 
      style={{ 
        position: "absolute", 
        transform: `rotate(${rotation}deg)`,
        opacity: 0.6,
        margin: "12px"
      }}
    >
      <path d="M0 0 L40 0 L40 4 L4 4 L4 40 L0 40 Z" fill={VINTAGE_COLORS.ink} />
      <circle cx="8" cy="8" r="2" fill={VINTAGE_COLORS.red} />
      <path d="M12 4 L12 12 L4 12" fill="none" stroke={VINTAGE_COLORS.ink} strokeWidth="1" />
    </svg>
  );
}

function MandalaBackdrop({ color }) {
  return (
    <div style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "120%",
      height: "120%",
      opacity: 0.1,
      pointerEvents: "none",
      zIndex: 0
    }}>
      <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", animation: "om-spin 120s linear infinite" }}>
        <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="0.5" strokeDasharray="1,2" />
        <circle cx="50" cy="50" r="35" fill="none" stroke={color} strokeWidth="1" strokeDasharray="5,5" />
        {Array.from({ length: 12 }).map((_, i) => (
          <g key={i} transform={`rotate(${i * 30} 50 50)`}>
            <path d="M50 10 L55 25 L45 25 Z" fill={color} />
            <circle cx="50" cy="5" r="1" fill={color} />
          </g>
        ))}
      </svg>
    </div>
  );
}

function PosterCard({ poster, onView }) {
  const [hovered, setHovered] = useState(false);
  const yugaColor = YUGA_COLORS[poster.yuga] || VINTAGE_COLORS.gold;

  return (
    <button
      onClick={() => onView(poster)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.12'/%3E%3C/svg%3E"), linear-gradient(180deg, ${VINTAGE_COLORS.paper} 0%, ${VINTAGE_COLORS.paperDark} 100%)`,
        backgroundBlendMode: "overlay",
        border: `1px solid ${VINTAGE_COLORS.ink}20`,
        color: VINTAGE_COLORS.ink,
        padding: "24px",
        cursor: "pointer",
        textAlign: "center",
        transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1)",
        position: "relative",
        overflow: "hidden",
        boxShadow: hovered
          ? `0 30px 60px rgba(0,0,0,0.3), inset 0 0 100px ${yugaColor}15`
          : `0 10px 30px rgba(0,0,0,0.1), inset 0 0 40px rgba(0,0,0,0.05)`,
        transform: hovered ? "scale(1.02) translateY(-8px)" : "scale(1) translateY(0)",
      }}
    >
      {/* Corner Decorations */}
      <div style={{ position: "absolute", top: 0, left: 0 }}><OrnateCorner rotation={0} /></div>
      <div style={{ position: "absolute", top: 0, right: 0 }}><OrnateCorner rotation={90} /></div>
      <div style={{ position: "absolute", bottom: 0, left: 0 }}><OrnateCorner rotation={270} /></div>
      <div style={{ position: "absolute", bottom: 0, right: 0 }}><OrnateCorner rotation={180} /></div>

      <MandalaBackdrop color={yugaColor} />

      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{
          fontFamily: "var(--mono)",
          fontSize: "9px",
          letterSpacing: "0.4em",
          color: VINTAGE_COLORS.red,
          textTransform: "uppercase",
          marginBottom: "16px",
          opacity: 0.8
        }}>
          {poster.yuga} yuga · sacred relic
        </div>

        <div style={{
          fontFamily: "var(--deva)",
          fontSize: "72px",
          color: VINTAGE_COLORS.ink,
          textShadow: `0 0 20px ${yugaColor}30`,
          lineHeight: 1,
          marginBottom: "20px",
        }}>
          {poster.deva}
        </div>

        <div style={{
          fontFamily: "var(--display)",
          fontStyle: "italic",
          fontSize: "18px",
          color: VINTAGE_COLORS.ink,
          marginBottom: "6px",
        }}>
          {poster.title}
        </div>

        <div style={{
          fontFamily: "var(--display)",
          fontSize: "10px",
          letterSpacing: "0.15em",
          color: VINTAGE_COLORS.inkFaded,
          textTransform: "uppercase",
          borderTop: `1px solid ${VINTAGE_COLORS.ink}20`,
          paddingTop: "12px",
          display: "inline-block",
          marginTop: "12px"
        }}>
          Free Download
        </div>
      </div>
    </button>
  );
}

function PosterViewer({ poster, onClose, onDownload }) {
  const [downloaded, setDownloaded] = useState(false);
  const yugaColor = YUGA_COLORS[poster.yuga] || VINTAGE_COLORS.gold;

  const handleDownload = () => {
    onDownload(poster);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 3000);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 350,
      background: "rgba(8,7,6,0.98)",
      backdropFilter: "blur(12px)",
      overflowY: "auto",
      padding: "60px 20px",
    }}>
      <button
        onClick={onClose}
        style={{
          position: "fixed", top: "24px", right: "24px",
          background: "transparent",
          border: `1px solid ${VINTAGE_COLORS.paper}40`,
          color: VINTAGE_COLORS.paper,
          width: "44px", height: "44px",
          borderRadius: "50%",
          fontSize: "20px",
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 300ms",
          zIndex: 10
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = VINTAGE_COLORS.paper}
        onMouseLeave={e => e.currentTarget.style.borderColor = `${VINTAGE_COLORS.paper}40`}
      >
        ×
      </button>

      <div style={{
        maxWidth: "1100px", margin: "0 auto",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
        gap: "60px",
        alignItems: "center",
      }}>
        {/* The Poster Relic */}
        <div style={{
          background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.15'/%3E%3C/svg%3E"), linear-gradient(180deg, ${VINTAGE_COLORS.paper} 0%, ${VINTAGE_COLORS.paperDark} 100%)`,
          backgroundBlendMode: "overlay",
          padding: "80px 60px",
          position: "relative",
          boxShadow: `0 40px 100px rgba(0,0,0,0.6), 0 0 40px ${yugaColor}20`,
          aspectRatio: "1 / 1.4",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center"
        }}>
          <div style={{ position: "absolute", top: 0, left: 0 }}><OrnateCorner rotation={0} /></div>
          <div style={{ position: "absolute", top: 0, right: 0 }}><OrnateCorner rotation={90} /></div>
          <div style={{ position: "absolute", bottom: 0, left: 0 }}><OrnateCorner rotation={270} /></div>
          <div style={{ position: "absolute", bottom: 0, right: 0 }}><OrnateCorner rotation={180} /></div>
          
          <MandalaBackdrop color={yugaColor} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{
              fontFamily: "var(--display)",
              fontSize: "14px",
              letterSpacing: "0.6em",
              color: VINTAGE_COLORS.inkFaded,
              textTransform: "uppercase",
              marginBottom: "40px",
            }}>
              Sanatana Chronicles
            </div>

            <div style={{
              fontFamily: "var(--deva)",
              fontSize: "180px",
              color: VINTAGE_COLORS.ink,
              textShadow: `0 0 40px ${yugaColor}40`,
              lineHeight: 1,
              marginBottom: "40px",
            }}>
              {poster.deva}
            </div>

            <div style={{
              fontFamily: "var(--display)",
              fontStyle: "italic",
              fontSize: "36px",
              color: VINTAGE_COLORS.ink,
              marginBottom: "20px",
            }}>
              {poster.title}
            </div>

            <div style={{
              width: "120px",
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${VINTAGE_COLORS.ink}, transparent)`,
              margin: "0 auto 30px auto"
            }} />

            <div style={{
              fontFamily: "var(--display)",
              fontSize: "12px",
              letterSpacing: "0.2em",
              color: VINTAGE_COLORS.red,
              textTransform: "uppercase",
            }}>
              {poster.verseRef}
            </div>
          </div>
        </div>

        {/* Content & Action */}
        <div style={{ color: VINTAGE_COLORS.paper }}>
          <h2 style={{
            fontFamily: "var(--display)",
            fontStyle: "italic",
            fontSize: "42px",
            marginBottom: "12px",
            color: yugaColor,
          }}>
            {poster.title}
          </h2>
          <div style={{
            fontFamily: "var(--display)",
            fontSize: "14px",
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: "var(--paper-dim)",
            marginBottom: "40px",
          }}>
            {poster.subtitle}
          </div>

          <div style={{
            fontFamily: "var(--deva)",
            fontSize: "32px",
            color: VINTAGE_COLORS.paper,
            lineHeight: 1.6,
            marginBottom: "24px",
            padding: "32px",
            background: "rgba(255,255,255,0.03)",
            borderLeft: `4px solid ${yugaColor}`,
          }}>
            {poster.verse}
          </div>

          <div style={{
            fontFamily: "var(--display)",
            fontStyle: "italic",
            fontSize: "18px",
            lineHeight: 1.6,
            color: "var(--paper-dim)",
            marginBottom: "32px",
            paddingLeft: "32px",
          }}>
            — {poster.verseTrans}
          </div>

          <p style={{
            fontFamily: "var(--body)",
            fontSize: "17px",
            lineHeight: 1.8,
            color: VINTAGE_COLORS.paper,
            opacity: 0.8,
            marginBottom: "48px",
          }}>
            {poster.meaning}
          </p>

          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <button
              onClick={handleDownload}
              disabled={downloaded}
              style={{
                flex: 1,
                padding: "20px",
                background: downloaded ? "#4a6a4a" : yugaColor,
                border: "none",
                color: "#0a0606",
                fontFamily: "var(--display)",
                fontSize: "14px",
                letterSpacing: "0.4em",
                fontWeight: "bold",
                textTransform: "uppercase",
                cursor: downloaded ? "default" : "pointer",
                transition: "all 300ms",
                boxShadow: `0 10px 30px ${yugaColor}40`,
              }}
            >
              {downloaded ? "✓ Saved to scrolls" : "Download Scripture"}
            </button>
          </div>

          <p style={{
            fontFamily: "var(--display)",
            fontSize: "10px",
            letterSpacing: "0.2em",
            color: "var(--paper-dim)",
            textAlign: "center",
            marginTop: "24px",
            textTransform: "uppercase"
          }}>
            High-Resolution SVG · Open Source · Dharma for all
          </p>
        </div>
      </div>
    </div>
  );
}

export function PostersSection({ onViewPoster }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      gap: "32px",
    }}>
      {POSTERS.map(poster => (
        <PosterCard
          key={poster.id}
          poster={poster}
          onView={onViewPoster}
        />
      ))}
    </div>
  );
}

export function PosterViewerModal({ poster, onClose }) {
  const handleDownload = (poster) => {
    const yugaColor = YUGA_COLORS[poster.yuga] || "#d4a44a";

    // Helper to wrap text into multiple lines for SVG
    const wrapText = (text, maxChars) => {
      if (!text) return [];
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';
      
      words.forEach(word => {
        if ((currentLine + word).length > maxChars) {
          lines.push(currentLine.trim());
          currentLine = word + ' ';
        } else {
          currentLine += word + ' ';
        }
      });
      lines.push(currentLine.trim());
      return lines;
    };

    const verseLines = wrapText(poster.verse, 35);
    const transLines = wrapText(`— ${poster.verseTrans}`, 55);
    const meaningLines = wrapText(poster.meaning, 65);

    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 1200" style="background:#e8dcc4">
      <defs>
        <filter id="noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4"/>
          <feColorMatrix type="saturate" values="0"/>
          <feBlend in="SourceGraphic" mode="multiply" result="blend"/>
        </filter>
      </defs>
      
      <rect width="800" height="1200" fill="#e8dcc4"/>
      <rect width="800" height="1200" fill="#d4c4a8" opacity="0.3"/>
      
      <!-- Ornate Corners -->
      <path d="M40 40 L80 40 L80 44 L44 44 L44 80 L40 80 Z" fill="#2a1810"/>
      <path d="M760 40 L720 40 L720 44 L756 44 L756 80 L760 80 Z" fill="#2a1810"/>
      <path d="M40 1160 L80 1160 L80 1156 L44 1156 L44 1120 L40 1120 Z" fill="#2a1810"/>
      <path d="M760 1160 L720 1160 L720 1156 L756 1156 L756 1120 L760 1120 Z" fill="#2a1810"/>

      <!-- Mandala Circle -->
      <circle cx="400" cy="400" r="280" fill="none" stroke="${yugaColor}" stroke-width="0.5" stroke-dasharray="4,4" opacity="0.4"/>
      <circle cx="400" cy="400" r="220" fill="none" stroke="${yugaColor}" stroke-width="1" stroke-dasharray="10,10" opacity="0.3"/>

      <text x="400" y="100" text-anchor="middle" font-family="serif" font-size="14" fill="#5a4030" letter-spacing="12">SANATANA CHRONICLES</text>
      
      <text x="400" y="420" text-anchor="middle" font-family="serif" font-size="220" fill="#2a1810" opacity="0.9">${poster.deva}</text>
      
      <text x="400" y="600" text-anchor="middle" font-family="serif" font-style="italic" font-size="42" fill="#2a1810">${poster.title}</text>
      
      <line x1="300" y1="630" x2="500" y2="630" stroke="#2a1810" stroke-width="2"/>
      
      <text x="400" y="680" text-anchor="middle" font-family="serif" font-size="16" fill="#8b2500" letter-spacing="6">${poster.verseRef.toUpperCase()}</text>
      
      <text x="400" y="780" text-anchor="middle" font-family="serif" font-size="28" fill="#2a1810">
        ${verseLines.map((line, i) => `<tspan x="400" dy="${i === 0 ? 0 : 40}">${line}</tspan>`).join('')}
      </text>
      
      <text x="400" y="${780 + (verseLines.length * 40) + 20}" text-anchor="middle" font-family="serif" font-style="italic" font-size="18" fill="#5a4030">
        ${transLines.map((line, i) => `<tspan x="400" dy="${i === 0 ? 0 : 25}">${line}</tspan>`).join('')}
      </text>
      
      <text x="400" y="${780 + (verseLines.length * 40) + (transLines.length * 25) + 80}" text-anchor="middle" font-family="serif" font-size="16" fill="#2a1810">
        ${meaningLines.map((line, i) => `<tspan x="400" dy="${i === 0 ? 0 : 22}">${line}</tspan>`).join('')}
      </text>
      
      <text x="400" y="1150" text-anchor="middle" font-family="serif" font-size="12" fill="#5a4030" letter-spacing="4">DHARMA FOR ALL · SANATANACHRONICLES.COM</text>
    </svg>`;

    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${poster.id}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return <PosterViewer poster={poster} onClose={onClose} onDownload={handleDownload} />;
}